// src/lib/signedUrl.js — Helpers para gerar signed URLs de storage privado.
//
// Bucket obras-files (e outros privados) precisam de URL assinada com expiração.
// Otimizações (perf shell-cliente):
//   1) signFotosUrls assina em LOTE via createSignedUrls (1 requisição por bucket,
//      em vez de N requisições — evita o efeito "espera o sign mais lento de 6").
//   2) Cache em memória das signed URLs por "bucket/path" (não reassina em re-render
//      nem ao trocar de aba, enquanto a URL ainda é válida).
//
// Uso:
//   import { signedUrl, signFotosUrls } from '../../lib/signedUrl.js';
//   const u = await signedUrl(foto.url);     // string -> string
//   await signFotosUrls(arr);                // muta arr[i].url
//   await signFotosUrls(arr, 'arquivo_url'); // muta arr[i].arquivo_url

import { sb } from '../api/supabaseClient.js';

// Cache de signed URLs: key "bucket/path" -> { url, exp(ms) }
const _cache = new Map();
const _now = () => Date.now();
const _cacheGet = (key) => { const h = _cache.get(key); return (h && h.exp > _now()) ? h.url : null; };
const _cacheSet = (key, url, expiresIn) => { _cache.set(key, { url, exp: _now() + Math.max(30, expiresIn - 60) * 1000 }); };

// Extrai { bucket, path } de uma URL pública ou path puro.
// Retorna { signed:true } se a URL já está assinada; null se não der pra extrair.
function _parse(publicOrPath) {
  if (!publicOrPath) return null;
  if (publicOrPath.includes('/object/sign/') || publicOrPath.includes('token=')) return { signed: true };
  const m = publicOrPath.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
  if (m) return { bucket: m[1], path: decodeURIComponent(m[2]) };
  if (!publicOrPath.startsWith('http')) return { bucket: 'obras-files', path: publicOrPath };
  return null;
}

/**
 * Troca uma URL pública (ou path puro) por uma signed URL com expiração.
 * Usa cache; se já é signed ou a extração falha, retorna o original.
 * @param {string} publicOrPath
 * @param {number} [expiresIn=3600]
 * @returns {Promise<string>}
 */
export async function signedUrl(publicOrPath, expiresIn = 3600) {
  if (!publicOrPath) return publicOrPath;
  const p = _parse(publicOrPath);
  if (!p || p.signed) return publicOrPath;
  const key = p.bucket + '/' + p.path;
  const cached = _cacheGet(key);
  if (cached) return cached;
  try {
    const { data, error } = await sb.storage.from(p.bucket).createSignedUrl(p.path, expiresIn);
    if (error || !data?.signedUrl) return publicOrPath;
    _cacheSet(key, data.signedUrl, expiresIn);
    return data.signedUrl;
  } catch {
    return publicOrPath;
  }
}

/**
 * Abre um arquivo de storage em nova aba, assinando a URL antes (buckets privados).
 * @param {string} url
 */
export async function abrirArquivoSeguro(url) {
  if (!url) return;
  let final = url;
  try { final = await signedUrl(url, 3600); } catch (_) { /* fallback */ }
  window.open(final, '_blank', 'noopener');
}
try { if (typeof window !== 'undefined') window.abrirArquivoSeguro = abrirArquivoSeguro; } catch (_) {}

/**
 * Assina, EM LOTE, o campo de URL de um array de objetos (muta in-place).
 * Agrupa por bucket e faz 1 requisição createSignedUrls por bucket.
 * Pula itens já assinados ou já em cache.
 *
 * @param {Array<object>} arr
 * @param {string} [field='url']
 * @param {number} [expiresIn=3600]
 * @returns {Promise<Array>}
 */
export async function signFotosUrls(arr, field = 'url', expiresIn = 3600) {
  if (!arr?.length) return arr;
  const byBucket = new Map(); // bucket -> [{ item, path }]
  for (const x of arr) {
    if (!x || !x[field]) continue;
    const p = _parse(x[field]);
    if (!p || p.signed) continue;
    const key = p.bucket + '/' + p.path;
    const cached = _cacheGet(key);
    if (cached) { x[field] = cached; continue; }
    if (!byBucket.has(p.bucket)) byBucket.set(p.bucket, []);
    byBucket.get(p.bucket).push({ item: x, path: p.path });
  }
  if (!byBucket.size) return arr;
  await Promise.all([...byBucket.entries()].map(async ([bucket, entries]) => {
    const paths = entries.map((e) => e.path);
    try {
      const { data, error } = await sb.storage.from(bucket).createSignedUrls(paths, expiresIn);
      if (error || !Array.isArray(data)) return;
      const urlByPath = new Map();
      data.forEach((res) => { if (res && res.signedUrl && !res.error && res.path != null) urlByPath.set(res.path, res.signedUrl); });
      entries.forEach((ent) => {
        const u = urlByPath.get(ent.path);
        if (u) { ent.item[field] = u; _cacheSet(bucket + '/' + ent.path, u, expiresIn); }
      });
    } catch { /* mantém original em caso de falha */ }
  }));
  return arr;
}
